import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  UserCheck, 
  BarChart3, 
  X,
  Shield,
  Trophy,
  ClipboardCheck,
  Database,
  MapPin,
  Calendar,
  FileText,
  Award,
  Grid3x3,
  DoorOpen,
  GraduationCap,
  Coffee,
  ClipboardList,
  Dumbbell,
  BookOpen
} from 'lucide-react';
import { useAuth } from '../App';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [appVersion, setAppVersion] = useState('1.80');

  const navigation = [
    {
      name: 'Dashboard',
      href: '/',
      icon: Home,
      roles: ['admin', 'management', 'nachwuchsleiter', 'trainer', 'leser']
    },
    {
      name: 'Teams',
      href: '/teams',
      icon: Trophy,
      roles: ['admin', 'management', 'nachwuchsleiter', 'trainer', 'leser']
    },
    {
      name: 'Spieler',
      href: '/players',
      icon: Users,
      roles: ['admin', 'management', 'nachwuchsleiter', 'trainer', 'leser']
    },
    {
      name: 'Trainingsanwesenheit',
      href: '/training-statistics',
      icon: ClipboardCheck,
      roles: ['admin', 'management', 'nachwuchsleiter', 'trainer', 'leser']
    },
    {
      name: 'Spiel/Platz Termine',
      href: '/game-schedule',
      icon: Calendar,
      roles: ['admin', 'management', 'nachwuchsleiter', 'trainer', 'leser', 'torwarttrainer', 'betreuer', 'platzwart']
    },
    {
      name: 'Matchlogs',
      href: '/matchlogs',
      icon: ClipboardList,
      roles: ['admin', 'management', 'nachwuchsleiter', 'trainer', 'torwarttrainer']
    },
    {
      name: 'Players IQ',
      href: '/player-evaluation',
      icon: BarChart3,
      roles: ['admin', 'management', 'nachwuchsleiter', 'trainer', 'leser', 'torwarttrainer']
    },
    {
      name: 'Trainingsübungen',
      href: '/training-exercises',
      icon: Dumbbell,
      roles: ['admin', 'management', 'nachwuchsleiter', 'trainer', 'torwarttrainer', 'betreuer']
    },
    {
      name: 'Kabineneinteilung',
      href: '/changing-rooms',
      icon: DoorOpen,
      roles: ['admin', 'management', 'nachwuchsleiter', 'trainer', 'torwarttrainer']
    },
    {
      name: 'Meetingpoint',
      href: '/meeting-point',
      icon: Coffee,
      roles: ['admin', 'management', 'nachwuchsleiter', 'trainer', 'torwarttrainer']
    },
    {
      name: 'Torwarttraining',
      href: '/goalkeeper-training',
      icon: GraduationCap,
      roles: ['admin', 'management', 'nachwuchsleiter', 'trainer', 'leser', 'torwarttrainer']
    },
    {
      name: 'Sponsoren',
      href: '/sponsors',
      icon: Award,
      roles: ['admin', 'management']
    },
    {
      name: '100er Tafel',
      href: '/hundertertafel',
      icon: Grid3x3,
      roles: ['admin', 'management']
    },
    {
      name: 'Nachwuchs-Budget',
      href: '/youth-budget',
      icon: Trophy,
      roles: ['admin', 'management', 'nachwuchsleiter']
    },
    {
      name: 'Datenbank',
      href: '/database',
      icon: Database,
      roles: ['admin']
    },
    {
      name: 'Vereins-Wiki',
      href: '/wiki',
      icon: BookOpen,
      roles: ['admin', 'management']
    },
    {
      name: 'Benutzer',
      href: '/users',
      icon: Shield,
      roles: ['admin']  // Only admin, not management
    },
    {
      name: 'Logs',
      href: '/logs',
      icon: FileText,
      roles: ['admin']  // Only admin
    }
  ];

  // Fetch app version on mount
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setAppVersion(response.data.version);
      } catch (error) {
        console.error('Fehler beim Laden der App-Version:', error);
        // Keep default version if fetch fails
      }
    };

    fetchVersion();
  }, []);

  const filteredNavigation = navigation.filter(item => 
    item.roles.includes(user?.role)
  );

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full max-h-screen">
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8">
                <img 
                  src="/skjenbach-logo.png" 
                  alt="SK Jenbach Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium">Version {appVersion}</span>
                <h2 className="text-lg font-semibold text-gray-900 leading-tight">SK Jenbach</h2>
              </div>
            </div>
            
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 lg:hidden"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200
                    ${isActive 
                      ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg' 
                      : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                    }
                  `}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-white' : ''}`} />
                  <span>{item.name}</span>
                </NavLink>
              );
            })}
          </nav>

          {/* User info footer - Fixed at bottom */}
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-white">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {user?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.username}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.role === 'admin' ? 'Administrator' : 
                   user?.role === 'management' ? 'Management' :
                   user?.role === 'trainer' ? 'Trainer' : 
                   user?.role === 'nachwuchsleiter' ? 'Nachwuchsleiter' :
                   user?.role === 'torwarttrainer' ? 'Torwarttrainer' :
                   user?.role === 'platzwart' ? 'Platzwart' : 'Leser'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;